import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { PATH_URL } from 'src/helpers/path'
import { getQueryParameters } from 'src/helpers/tool'
import styles from './styles.module.scss'

interface ProgressBarConfig {
  readonly QUIZ_STEPS: readonly string[]
  readonly SKIPPABLE_STEPS: readonly string[]
  readonly MIN_PROGRESS: number
  readonly MAX_PROGRESS: number
}

interface ProgressCalculationParams {
  currentPath: string
  hasCollectionId: boolean
  currentStepIndex: number
}

interface DebugInfo {
  currentPath: string
  currentStepIndex: number
  hasCollectionId: boolean
  progressPercent: number
  totalSteps: number
  calculationMethod: string
}

// Configuration
const PROGRESS_BAR_CONFIG: ProgressBarConfig = {
  QUIZ_STEPS: [
    PATH_URL.COLLECTIONS_QUIZ_COLOR_PALETTE,
    PATH_URL.COLLECTIONS_QUIZ_FLOWER_SELECTION,
    PATH_URL.COLLECTIONS_QUIZ_EMAIL_ENTRY,
    PATH_URL.COLLECTIONS_QUIZ_RESULT_CALCULATION,
    PATH_URL.COLLECTIONS_QUIZ_ESSENTIALS,
    PATH_URL.COLLECTIONS_QUIZ_PARTNER_FULL_NAME,
    PATH_URL.COLLECTIONS_QUIZ_DATE_PICKER,
    PATH_URL.COLLECTIONS_QUIZ_GUEST_COUNT,
    PATH_URL.COLLECTIONS_QUIZ_EXPECTED_BUDGET,
    // Removed party count and table count
    // PATH_URL.COLLECTIONS_QUIZ_PARTY_COUNT,
    // PATH_URL.COLLECTIONS_QUIZ_TABLE_COUNT,
    PATH_URL.COLLECTIONS_QUIZ_FLORAL_PLANNING,
  ] as const,
  SKIPPABLE_STEPS: [PATH_URL.COLLECTIONS_QUIZ_COLOR_PALETTE, PATH_URL.COLLECTIONS_QUIZ_FLOWER_SELECTION] as const,
  MIN_PROGRESS: 0,
  MAX_PROGRESS: 100,
} as const

// Utility functions
const getCollectionIdFromStorage = (): string | null => {
  try {
    const collectionsQuizEntry = localStorage.getItem('collectionsQuizEntry')
    if (!collectionsQuizEntry) return null

    const parsed = JSON.parse(collectionsQuizEntry)
    return parsed?.collectionId || null
  } catch (error) {
    console.warn('Failed to parse collectionsQuizEntry from localStorage:', error)
    return null
  }
}

const calculateProgress = ({ currentPath, hasCollectionId, currentStepIndex }: ProgressCalculationParams): number => {
  const { QUIZ_STEPS, SKIPPABLE_STEPS, MIN_PROGRESS, MAX_PROGRESS } = PROGRESS_BAR_CONFIG

  // Handle case where current step is not found
  if (currentStepIndex === -1) {
    return MIN_PROGRESS
  }

  let progressPercent: number

  if (hasCollectionId && SKIPPABLE_STEPS.includes(currentPath)) {
    progressPercent = 5
  } else if (hasCollectionId) {
    const totalStepsWithSkip = QUIZ_STEPS.length - SKIPPABLE_STEPS.length
    const adjustedCurrentIndex = Math.max(0, currentStepIndex - SKIPPABLE_STEPS.length)
    progressPercent = ((adjustedCurrentIndex + 1) / totalStepsWithSkip) * MAX_PROGRESS
  } else {
    progressPercent = ((currentStepIndex + 1) / QUIZ_STEPS.length) * MAX_PROGRESS
  }

  return Math.max(MIN_PROGRESS, Math.min(MAX_PROGRESS, progressPercent))
}

const logDebugInfo = (debugInfo: DebugInfo): void => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Progress Bar Debug:', debugInfo)
  }
}

// Main component
const CollectionsProgressBar: React.FC = () => {
  const location = useLocation()
  const [progress, setProgress] = useState<number>(0)

  // Memoized values
  const queryParams = useMemo(() => getQueryParameters(), [])
  const isTier2 = useMemo(() => queryParams.get('tier2') === 'true', [queryParams])

  const hasCollectionId = useMemo(() => {
    const urlCollectionId = queryParams.get('collectionId')
    const storageCollectionId = getCollectionIdFromStorage()
    return Boolean(urlCollectionId || storageCollectionId)
  }, [queryParams])

  // Calculate progress
  const currentStepIndex = useMemo(() => {
    return PROGRESS_BAR_CONFIG.QUIZ_STEPS.findIndex((step) => location.pathname === step)
  }, [location.pathname])

  // Update progress when dependencies change
  useEffect(() => {
    const progressPercent = calculateProgress({
      currentPath: location.pathname,
      hasCollectionId,
      currentStepIndex,
    })

    setProgress(progressPercent)

    // Log debug information in development
    const debugInfo: DebugInfo = {
      currentPath: location.pathname,
      currentStepIndex,
      hasCollectionId,
      progressPercent,
      totalSteps: hasCollectionId
        ? PROGRESS_BAR_CONFIG.QUIZ_STEPS.length - PROGRESS_BAR_CONFIG.SKIPPABLE_STEPS.length
        : PROGRESS_BAR_CONFIG.QUIZ_STEPS.length,
      calculationMethod:
        hasCollectionId && PROGRESS_BAR_CONFIG.SKIPPABLE_STEPS.includes(location.pathname)
          ? 'redirect'
          : hasCollectionId
          ? 'with-collection-skip'
          : 'normal-flow',
    }

    logDebugInfo(debugInfo)
  }, [location.pathname, hasCollectionId, currentStepIndex])

  const progressBarStyle = useMemo(
    () => ({
      width: `${progress}%`,
    }),
    [progress]
  )

  if (!isTier2) {
    return null
  }

  return (
    <div
      className={styles.progressBarContainer}
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className={styles.progressBarBackground}>
        <div className={styles.progressBarFill} style={progressBarStyle} data-testid="progress-bar-fill" />
      </div>
    </div>
  )
}

export default CollectionsProgressBar
